import { apiAuthCaller } from '@services/apiCaller';
import auth, { CognitoTokens } from '@services/cognito';
import clerkAuth from '@services/clerk';
import { action, autorun, computed, observable, set, toJS } from 'mobx';
import LocalStorage from '@utils/localStorage';
import { Auth } from 'aws-amplify';
import { IRootStore } from '@stores/index';
import { bugsnagSetUser } from '@lib/bugsnag';
import { zendesk } from '@components/common/util/support';
import { loadStores } from '@stores/util';
import { analytics } from '@lib/analytics';
import { BI_EVENTS, CATEGORY, TRACK_TYPE } from '@lib/analyticsEvents';
import { CatchErr } from '@utils/types';
import { IMemberStore } from '../member';
import { getUserTraits, mapToWorkspaceProperties, User } from './util';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const serializeJavascript = require('serialize-javascript');

export enum LoginMfaState {
  None = '',
  ForcedSetup = 'mfa_setup',
}

interface IMfaData {
  sessionToken?: string;
  status?: string;
  email?: string;
  provider?: string;
  providerUserId?: string;
  state?: LoginMfaState;
}

interface IUserData {
  data: {
    clerkId: string;
    createdAt: string;
    dataplaneUrl: string;
    email: string;
    hash: string;
    id: string;
    name: string;
    organization: string;
    provider: string;
    providerUserId: string;
    tokenVersion: number;
    verificationToken: string;
    verified: boolean;
    workspaces: IWorkspace[];
    phoneNumber: string;
    mfaStatus: string;
    enforcedMfa: boolean;
    userWorkspaceProperties: {
      workspaceId: string;
      onboardingStatus: number;
    }[];
  };
}

export type TrackUserLogin = Partial<
  Pick<
    User,
    'invited' | 'churnSource' | 'migratedFrom' | 'firstTimeLogin' | 'workSource' | 'otherWorkSource'
  >
>;

interface IWorkspace {
  dataPlaneURL: string | null;
  enableTeams: boolean;
  enterprise: boolean;
  freetrial: boolean;
  hosted: boolean;
  id: string;
  name: string;
  organizationId: string;
  pro: boolean;
}

export type SessionData = IMfaData & CognitoTokens;

export interface IUserStore {
  setUserSessionForMFA(email: string, mfaData: Partial<SessionData>): void;
  setAndRefreshToken(token: string): Promise<void>;
  id: string;
  name: string;
  email: string;
  token: string;
  dataPlaneUrl: string;
  loggedIn: boolean;
  invitedUserFirstTimeLogin: boolean;
  firstTimeLogin: boolean;
  sessionData: SessionData;
  workspaces: Array<IWorkspace> | undefined;
  selectedWorkspaceId?: string;
  isAdmin: boolean;
  isTeamsAdmin: boolean;
  role: string;
  rootStore: IRootStore;
  provider: string;
  providerUserId: string;
  mfaData: IMfaData | undefined;
  phoneNumber: string;
  mfaStatus: string;
  enforcedMfa: boolean;
  isOpenSource: boolean;
  signUp(name: string, organization: string, email?: string): void;
  setName(name: string): void;
  updateEnforcedMfa(): Promise<void>;
  loginUser(sessionData: SessionData): Promise<void>;
  trackUserLogin(userLogin?: TrackUserLogin): Promise<void>;
  setProvider(provider: string, providerUserId: string): void;
  signOutUser(signOutfromBackend?: boolean, globalSignout?: boolean): void;
  switchWorkspace(workspaceId: string): void;
  setPhoneNumber(number: string): void;
  setMfaStatus(value: string): void;
  loginOrAppRefresh(): void;
  isSso: boolean;
  isGoogleOrSso: boolean;
  properties: { onboarding: number | undefined } | undefined;
  setProperties: (status: number) => void;
}

// TODO: Refactor code related to persistance
function autoSave(store: UserStore) {
  let firstRun = true;
  autorun(() => {
    const userStore: Partial<UserStore> = toJS(store);
    delete userStore.rootStore;
    const json = serializeJavascript({ ...userStore, firstTimeLogin: false }, { isJSON: true });
    if (!firstRun) {
      LocalStorage.set('userStore', json);
    }
    firstRun = false;
  });
}

interface WorkspaceProperties {
  onboarding: Record<string, number>;
}

const SAVED_WORKSPACES = 'savedWorkspaces';

const SIGN_OUT_FAILURE = 'User Sign out failed';

export class UserStore implements IUserStore {
  @observable public id = '';

  @observable public name = '';

  @observable public email = '';

  @observable public token = '';

  @observable public dataPlaneUrl = '';

  @observable public sessionData: SessionData = {
    accessToken: '',
    idToken: '',
    refreshToken: '',
  };

  @observable public loggedIn = false;

  @observable public invitedUserFirstTimeLogin = false;

  @observable public firstTimeLogin = false;

  @observable public workspaces: IWorkspace[] | undefined;

  @observable public selectedWorkspaceId?: string;

  @observable public rootStore: IRootStore;

  @observable public provider = '';

  @observable public providerUserId = '';

  @observable public mfaData: IMfaData | undefined;

  @observable phoneNumber = '';

  @observable mfaStatus = '';

  @observable enforcedMfa = false;

  @observable workspaceProperties: WorkspaceProperties | undefined;

  constructor(rootStore: IRootStore) {
    this.load();
    autoSave(this);
    this.rootStore = rootStore;

    // refreshing the stores
    setInterval(async () => {
      await this.getAllData();
    }, 15 * 60 * 1000);
  }

  public load() {
    const userStore = LocalStorage.get('userStore');
    if (userStore) {
      const store: IUserStore = JSON.parse(userStore);
      set(this, store);
    }
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @action.bound
  public setProvider(provider: string, providerUserId: string): void {
    this.provider = provider;
    this.providerUserId = providerUserId;
  }

  @action.bound
  public setPhoneNumber(number: string): void {
    this.phoneNumber = number;
  }

  @action.bound
  public setMfaStatus(value: string): void {
    this.mfaStatus = value;
  }

  @action.bound
  public signUp(name: string, organization: string, email?: string) {
    this.loggedIn = false;
    this.name = name;
    this.rootStore.organizationStore.name = organization;
    if (email) {
      this.email = email;
    }
  }

  @action.bound async setAndRefreshToken(idToken: string) {
    if (idToken !== this.token) {
      this.token = idToken;
    }
    let shouldSignOut = false;
    // Gets current session. Tokens are refreshed by amplify automatically when they expire
    try {
      const res = await auth.currentSession(this.sessionData, this.email);

      if (res.status === 'success' && res.idToken) {
        this.token = res.idToken;
        // TODO - remove this.token and use from sessionData
        this.sessionData.idToken = res.idToken;

        this.sessionData.accessToken = res.accessToken;
        this.sessionData.refreshToken = res.refreshToken;
      }
    } catch (error: CatchErr) {
      if (error && error.response && error.response.status === 400) {
        shouldSignOut = true;
      }
    }

    // doing the same thing as error response interceptor
    if (shouldSignOut) {
      await this.signOutUser();
    }
  }

  @action.bound
  public setUserSessionForMFA(email: string, mfaData: IMfaData) {
    this.email = email;
    this.mfaData = mfaData;
    this.sessionData.idToken = mfaData.sessionToken as string;
  }

  @action.bound
  public loginOrAppRefresh() {
    this.getUser();
  }

  @action.bound
  async updateEnforcedMfa() {
    const userData: IUserData = await apiAuthCaller(this.token, true).get('/getUser');
    const { enforcedMfa } = userData.data;
    this.enforcedMfa = enforcedMfa;
  }

  @action.bound
  async loginUser(sessionData: SessionData) {
    this.setSessionToken(sessionData);
    await this.getUser();
  }

  private async getUser() {
    const userData: IUserData = await apiAuthCaller(this.token, true).get(
      '/getUser?includeWorkspaceProperties=true',
    );
    const {
      id,
      name,
      email,
      phoneNumber,
      mfaStatus,
      enforcedMfa,
      workspaces,
      dataplaneUrl,
      userWorkspaceProperties,
    } = userData.data;
    this.id = id;
    this.email = email;
    this.name = name;
    this.phoneNumber = phoneNumber;
    this.mfaStatus = mfaStatus;
    this.enforcedMfa = enforcedMfa;
    this.workspaces = workspaces;
    this.dataPlaneUrl = dataplaneUrl;
    this.selectedWorkspaceId = this.getInitialWorkspaceId();
    this.updateSavedWorkspaces();
    this.workspaceProperties = mapToWorkspaceProperties(userWorkspaceProperties);
    this.mfaData = {};
    this.loggedIn = true;
    bugsnagSetUser(this);
  }

  private setSessionToken(session: SessionData) {
    this.sessionData = session;
    this.token = this.sessionData.idToken;
    // For Cognito, this.token isn't set until an unauthorized
    // call triggers this.setAndRefreshToken runs. Clerk needs
    // this.token set immediately since some front end calls
    // use this.token directly instead of sessionData.idToken

    if (session.idToken === 'Clerk') {
      this.token = 'Clerk';
    }
  }

  @action.bound
  async trackUserLogin({
    invited,
    churnSource,
    migratedFrom,
    workSource,
    otherWorkSource,
    firstTimeLogin = false,
  }: TrackUserLogin) {
    this.invitedUserFirstTimeLogin = firstTimeLogin && !!invited;
    this.firstTimeLogin = firstTimeLogin;
    const traits = getUserTraits({
      invited,
      churnSource,
      migratedFrom,
      workSource,
      otherWorkSource,
      firstTimeLogin,
      userId: this.id,
      name: this.name,
      email: this.email,
      organization: this.rootStore.organizationStore.name,
      workspaceId: this.selectedWorkspaceId,
    });
    window.rudderanalytics.identify(this.email, traits, {
      integrations: {
        Salesforce: true,
      },
    });

    if (firstTimeLogin) {
      analytics.track(BI_EVENTS.SIGNED_UP, TRACK_TYPE.CLICK, CATEGORY.AUTH);
      analytics.track(BI_EVENTS.SIGNED_UP1, TRACK_TYPE.CLICK, CATEGORY.AUTH);
    }
    zendesk.inject();
    zendesk.disable();
  }

  private getInitialWorkspaceId() {
    if (!this.workspaces || this.workspaces.length === 0) {
      return '';
    }
    const savedWorkspaces = LocalStorage.get(SAVED_WORKSPACES);
    if (savedWorkspaces) {
      const workspaceId = JSON.parse(savedWorkspaces)[this.email];
      const workspaceExists = this.workspaces.some((x: IWorkspace) => x.id === workspaceId);
      if (workspaceExists) {
        return workspaceId;
      }
    }
    return this.workspaces[this.workspaces.length - 1].id;
  }

  private async signOutFromBackend(globalSignout = false): Promise<void> {
    try {
      await apiAuthCaller(this.sessionData.idToken).post(
        '/signout',
        {},
        { params: { globalSignout } },
      );
    } catch (err) {
      const traits = {
        id: this.id,
        name: this.name,
        email: this.email,
      };
      window.rudderanalytics.track(SIGN_OUT_FAILURE, {
        traits,
        error: err,
      });
    }

    if (globalSignout) {
      await this.setAndRefreshToken(this.sessionData.idToken);
    }
  }

  @action.bound
  public async signOutUser(signOutfromBackend = false, globalSignout = false) {
    zendesk.delete();
    this.updateSavedWorkspaces();
    const traits = {
      id: this.id,
      name: this.name,
      email: this.email,
    };

    if (signOutfromBackend) {
      await this.signOutFromBackend(globalSignout);
    }

    this.loggedIn = false;
    this.id = '';
    this.name = '';
    this.email = '';
    this.token = '';
    this.sessionData = {
      accessToken: '',
      refreshToken: '',
      idToken: '',
    };
    this.provider = '';
    this.providerUserId = '';
    this.workspaces = [];
    this.selectedWorkspaceId = '';
    this.rootStore.sourcesListStore.sources = [];
    this.rootStore.sourcesListStore.firstLoad = false;
    this.rootStore.modelListStore.firstLoad = false;
    this.rootStore.destinationsListStore.destinations = [];
    this.rootStore.destinationsListStore.firstLoad = false;
    this.rootStore.connectionsStore.connections = {};
    this.rootStore.workspaceStore.reset();
    bugsnagSetUser(this);
    try {
      await clerkAuth.signout();
      window.rudderanalytics.track('User Signed out', traits);
    } catch (err) {
      window.rudderanalytics.track(SIGN_OUT_FAILURE, {
        traits,
        error: err,
      });
    }
    try {
      await Auth.signOut();
      window.rudderanalytics.track('User Signed out', traits);
    } catch (err) {
      window.rudderanalytics.track(SIGN_OUT_FAILURE, {
        traits,
        error: err,
      });
    }
  }

  private updateSavedWorkspaces() {
    const storedWorkspaces = LocalStorage.get(SAVED_WORKSPACES);
    const workspaces = storedWorkspaces ? JSON.parse(storedWorkspaces) : {};
    const newWorkspaces = { ...workspaces, [this.email]: this.selectedWorkspaceId };
    LocalStorage.set(SAVED_WORKSPACES, JSON.stringify(newWorkspaces));
  }

  @action.bound
  public async switchWorkspace(workspaceId: string) {
    const {
      billingStore,
      sourcesListStore,
      destinationsListStore,
      modelListStore,
      workspaceStore,
      organizationStore,
    } = this.rootStore;
    workspaceStore.reset();
    organizationStore.reset();
    billingStore.clearBillingInfo();
    this.selectedWorkspaceId = workspaceId;
    this.updateSavedWorkspaces();
    sourcesListStore.sources = [];
    sourcesListStore.firstLoad = false;
    destinationsListStore.destinations = [];
    destinationsListStore.firstLoad = false;
    modelListStore.firstLoad = false;
    await this.getAllData();
  }

  @action.bound
  public async getAllData() {
    // check if user is logged in before doing any request
    if (this.loggedIn) {
      await loadStores();
    }
  }

  @action.bound setProperties(status: number) {
    if (this.workspaceProperties) {
      this.workspaceProperties.onboarding[this.selectedWorkspaceId || ''] = status;
    }
  }

  @computed get isAdmin() {
    const member = this.rootStore.workspaceStore.members.find(
      (member: IMemberStore) => member.email === this.email,
    );
    return member ? ['admin', 'read-write'].includes(member.role.toLowerCase()) : false;
  }

  @computed get isTeamsAdmin() {
    const member = this.rootStore.workspaceStore.members.find(
      (member: IMemberStore) => member.email === this.email,
    );
    return member ? member.role.toLowerCase() === 'admin' : false;
  }

  @computed get properties() {
    const status = this.workspaceProperties?.onboarding[this.selectedWorkspaceId || ''];
    return { onboarding: status };
  }

  @computed get role() {
    const member = this.rootStore.workspaceStore.members.find(
      (member: IMemberStore) => member.email === this.email,
    );
    return member ? member.role.toLowerCase() : '';
  }

  @computed get isOpenSource() {
    let openSource = true;
    if (this.workspaces && this.workspaces?.length > 0) {
      for (let i = 0; i < this.workspaces.length; i++) {
        openSource =
          !this.workspaces[i].freetrial &&
          !this.workspaces[i].pro &&
          !this.workspaces[i].enterprise;
        if (!openSource) {
          break;
        }
      }
    }
    return openSource;
  }

  @computed get isSso() {
    return this.token === 'Clerk' || (!!this.providerUserId && this.provider !== 'Google');
  }

  @computed get isGoogleOrSso() {
    if (this.isSso) {
      return true;
    }

    // Handle Google users logging in with password
    let decodedPayload = {} as Record<string, string>;
    try {
      const jwtPayload = this.token.split('.')[1] || '';
      decodedPayload = JSON.parse(atob(jwtPayload) || '{}');
    } catch (error) {
      return false;
    }

    return decodedPayload['cognito:username']?.startsWith('Google');
  }
}
